import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Move, Maximize2, Minimize2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Color mapping for activity types
const colorMap = {
  amber: { bg: '#fbbf24', border: '#f59e0b' },
  blue: { bg: '#3b82f6', border: '#2563eb' },
  red: { bg: '#ef4444', border: '#dc2626' },
  green: { bg: '#22c55e', border: '#16a34a' },
  teal: { bg: '#14b8a6', border: '#0d9488' },
  pink: { bg: '#ec4899', border: '#db2777' },
  purple: { bg: '#8b5cf6', border: '#7c3aed' },
};

// Create dynamic icon for an activity type
const createActivityIcon = (icon, color) => {
  const colors = colorMap[color] || colorMap.purple;
  return new L.DivIcon({
    className: 'custom-activity-marker',
    html: `<div style="background: ${colors.bg}; width: 32px; height: 32px; border-radius: 50%; border: 2px solid ${colors.border}; display: flex; align-items: center; justify-content: center; font-size: 16px; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">${icon || '📍'}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

// Center marker icon
const centerIcon = new L.DivIcon({
  className: 'custom-center-marker',
  html: '<div style="background: #8b5cf6; width: 36px; height: 36px; border-radius: 50%; border: 3px solid #7c3aed; display: flex; align-items: center; justify-content: center; font-size: 18px; box-shadow: 0 3px 8px rgba(0,0,0,0.4); cursor: move;">📍</div>',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

// DraggableMarker component for the center point
const DraggableMarker = ({ position, onDragEnd }) => {
  const markerRef = useRef(null);
  
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const newPos = marker.getLatLng();
        onDragEnd([newPos.lat, newPos.lng]);
      }
    },
  };

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={centerIcon}
    >
      <Popup>
        <div className="text-center">
          <p className="font-semibold text-stone-800">Search Center</p>
          <p className="text-xs text-stone-500">Drag to move</p>
        </div>
      </Popup>
    </Marker>
  );
};

// Component to handle map click events
const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
};

// Component to recenter map when center changes
const MapRecenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

const RadiusSearchMap = ({ posts, onRadiusSearch, initialCenter, initialRadius, activityTypes = [] }) => {
  const [center, setCenter] = useState(initialCenter || [-37.8136, 144.9631]); // Default: Melbourne
  const [radius, setRadius] = useState(initialRadius || 25); // Default 25km radius
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Date filter state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Build icon cache from activity types
  const activityIconCache = useMemo(() => {
    const cache = {};
    activityTypes.forEach(at => {
      const key = at.field_name || at.name.toLowerCase().replace(/\s+/g, '_');
      cache[key] = createActivityIcon(at.icon, at.color);
    });
    // Add default icons for backward compatibility
    if (!cache.harvesting) cache.harvesting = createActivityIcon('🍯', 'amber');
    if (!cache.feeding) cache.feeding = createActivityIcon('🍽️', 'blue');
    if (!cache.mites) cache.mites = createActivityIcon('🐛', 'red');
    return cache;
  }, [activityTypes]);

  // Get activity config from type name
  const getActivityConfig = useCallback((activityType) => {
    return activityTypes.find(at => 
      (at.field_name || at.name.toLowerCase().replace(/\s+/g, '_')) === activityType
    );
  }, [activityTypes]);

  // Get user's current location on mount
  useEffect(() => {
    if (!initialCenter && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          // Use default if geolocation fails
        }
      );
    }
  }, [initialCenter]);

  // Trigger search when center, radius, or dates change
  useEffect(() => {
    onRadiusSearch(center[0], center[1], radius, fromDate, toDate);
  }, [center, radius, fromDate, toDate, onRadiusSearch]);

  const handleCenterDrag = useCallback((newCenter) => {
    setCenter(newCenter);
  }, []);

  const handleMapClick = useCallback((newCenter) => {
    setCenter(newCenter);
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getActivityColor = (type) => {
    const config = getActivityConfig(type);
    if (config) {
      return colorMap[config.color]?.bg || colorMap.purple.bg;
    }
    // Fallback defaults
    switch (type) {
      case 'harvesting': return '#fbbf24';
      case 'feeding': return '#3b82f6';
      case 'mites': return '#ef4444';
      default: return '#8b5cf6';
    }
  };

  const getActivityLabel = (type) => {
    const config = getActivityConfig(type);
    if (config) {
      return `${config.icon || '📍'} ${config.name}`;
    }
    // Fallback defaults
    switch (type) {
      case 'harvesting': return '🍯 Harvesting';
      case 'feeding': return '🍽️ Feeding';
      case 'mites': return '🐛 Mites Alert';
      default: return '📍 Activity';
    }
  };

  const getActivityIcon = (type) => {
    return activityIconCache[type] || createActivityIcon('📍', 'purple');
  };

  return (
    <div className={`bg-white rounded-xl border border-stone-200 overflow-hidden transition-all ${isExpanded ? 'fixed inset-4 z-50' : ''}`}>
      {/* Controls Header */}
      <div className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-stone-200">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Move className="w-5 h-5 text-purple-600" />
              <div>
                <h3 className="font-semibold text-stone-800 text-sm">Drag to Search Area</h3>
                <p className="text-xs text-stone-500">Click map or drag pin to move center</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Radius Slider */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-600 whitespace-nowrap">Radius:</span>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  className="w-20 sm:w-32 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
                <span className="text-sm font-medium text-purple-700 min-w-[50px]">{radius} km</span>
              </div>
              
              {/* Expand/Collapse Button */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-white rounded-lg transition-colors"
                title={isExpanded ? 'Minimize' : 'Expand'}
              >
                {isExpanded ? (
                  <Minimize2 className="w-4 h-4 text-stone-600" />
                ) : (
                  <Maximize2 className="w-4 h-4 text-stone-600" />
                )}
              </button>
            </div>
          </div>
          
          {/* Date Filter Row */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-purple-100">
            <span className="text-xs text-stone-600 font-medium">📅 Filter by Date:</span>
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-2 py-1 text-xs border border-stone-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
              <span className="text-xs text-stone-400">to</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-2 py-1 text-xs border border-stone-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
            </div>
            {(fromDate || toDate) && (
              <button
                onClick={() => { setFromDate(''); setToDate(''); }}
                className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 rounded text-stone-600"
              >
                Clear
              </button>
            )}
            <span className="text-xs text-stone-400 ml-auto">
              {posts.length} result{posts.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className={`relative ${isExpanded ? 'h-[calc(100%-140px)]' : 'h-64 sm:h-80'}`}>
        <style>{`
          .leaflet-control-container { z-index: 400 !important; }
          .leaflet-top, .leaflet-bottom { z-index: 400 !important; }
          .leaflet-control-zoom { z-index: 400 !important; }
          .leaflet-control-zoom a { 
            z-index: 400 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          /* Ensure popup is visible and clickable */
          .leaflet-popup { z-index: 1000 !important; }
          .leaflet-popup-content-wrapper { 
            z-index: 1001 !important;
            overflow: visible !important;
          }
          .leaflet-popup-close-button {
            z-index: 1002 !important;
            position: absolute !important;
            top: 0 !important;
            right: 0 !important;
            width: 24px !important;
            height: 24px !important;
            font-size: 20px !important;
            line-height: 20px !important;
            text-align: center !important;
            color: #666 !important;
            background: white !important;
            border-radius: 0 12px 0 8px !important;
            padding: 2px 6px !important;
            cursor: pointer !important;
          }
          .leaflet-popup-close-button:hover {
            color: #333 !important;
            background: #f0f0f0 !important;
          }
          .leaflet-popup-content {
            margin: 8px 12px !important;
            margin-right: 20px !important;
          }
        `}</style>
        <MapContainer
          center={center}
          zoom={9}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapClickHandler onMapClick={handleMapClick} />
          <MapRecenter center={center} />
          
          {/* Search Radius Circle */}
          <Circle
            center={center}
            radius={radius * 1000} // Convert km to meters
            pathOptions={{
              color: '#8b5cf6',
              fillColor: '#8b5cf6',
              fillOpacity: 0.1,
              weight: 2,
              dashArray: '5, 5',
            }}
          />
          
          {/* Draggable Center Marker */}
          <DraggableMarker position={center} onDragEnd={handleCenterDrag} />
          
          {/* Activity Post Markers */}
          {posts.map((post) => {
            if (!post.gps_lat || !post.gps_lng || post.gps_lat === 0 || post.gps_lng === 0) {
              return null;
            }
            
            // Format the activity date
            const activityDate = post.created_at ? new Date(post.created_at) : null;
            const formattedActivityDate = activityDate 
              ? activityDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
              : null;
            
            return (
              <Marker
                key={post.id}
                position={[post.gps_lat, post.gps_lng]}
                icon={getActivityIcon(post.activity_type)}
              >
                <Popup>
                  <div className="min-w-[180px]">
                    <div className={`px-2 py-1 rounded-t text-xs font-medium text-white mb-2`} 
                         style={{ backgroundColor: getActivityColor(post.activity_type) }}>
                      {getActivityLabel(post.activity_type)}
                    </div>
                    <h4 className="font-semibold text-stone-800 text-sm">{post.user_name}</h4>
                    {post.user_company && (
                      <p className="text-xs text-stone-500">{post.user_company}</p>
                    )}
                    {post.location_name && (
                      <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {post.location_name.split(',')[0]}
                      </p>
                    )}
                    {post.distance_km !== undefined && (
                      <p className="text-xs text-purple-600 mt-1">
                        📏 {post.distance_km} km away
                      </p>
                    )}
                    {/* Activity Date - More Prominent */}
                    <div className="mt-2 pt-2 border-t border-stone-200">
                      <p className="text-xs font-medium text-stone-700">
                        📅 Started: {formattedActivityDate || 'Unknown'}
                      </p>
                      <p className="text-xs text-stone-400">{formatDate(post.created_at)}</p>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="p-2 bg-stone-50 border-t border-stone-200 flex flex-wrap items-center gap-3 text-xs">
        <span className="text-stone-500">Legend:</span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-purple-500"></span>
          Search Center
        </span>
        {activityTypes.filter(at => at.is_active !== false).slice(0, 5).map(at => {
          const colors = colorMap[at.color] || colorMap.purple;
          return (
            <span key={at.id || at.name} className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.bg }}></span>
              {at.icon} {at.name}
            </span>
          );
        })}
        {activityTypes.length > 5 && (
          <span className="text-stone-400">+{activityTypes.length - 5} more</span>
        )}
        <span className="text-stone-400 ml-auto">{posts.length} posts in area</span>
      </div>
    </div>
  );
};

export default RadiusSearchMap;
